import { useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  AlertTriangle,
  RefreshCw,
  Zap,
  Info,
  ArrowLeft,
  CheckSquare,
  Square,
  MinusSquare,
} from "lucide-react";
import {
  getPairQueue,
  getPairDetail,
  submitPairDecision,
  batchDecidePairs,
} from "../api/mdm-client";
import type { PairQueueItem, PairDetail } from "../api/mdm-client";

// ─── Helpers ────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 0.85) return "text-success";
  if (score >= 0.6) return "text-warning";
  return "text-error";
}

function scoreBg(score: number): string {
  if (score >= 0.85) return "bg-success/15 border-success/30";
  if (score >= 0.6) return "bg-warning/15 border-warning/30";
  return "bg-error/15 border-error/30";
}

function pct(v: number) {
  return `${Math.round(v * 100)}%`;
}

// ─── Field comparison row ────────────────────────────────────────────────────

function FieldRow({
  field,
  leftVal,
  rightVal,
  score,
  method,
  weight,
}: {
  field: string;
  leftVal: string;
  rightVal: string;
  score: number;
  method: string;
  weight: number;
}) {
  const match = score >= 0.85;
  const partial = score >= 0.5 && score < 0.85;
  return (
    <tr className="border-b border-[var(--border-subtle)] hover:bg-[var(--overlay-faint)]">
      <td className="py-2 px-3 text-xs font-mono text-white/50 whitespace-nowrap">
        {field}
      </td>
      <td className="py-2 px-3 text-sm text-white/90 max-w-[180px] truncate">
        {leftVal || (
          <span className="text-[var(--text-primary)] italic">—</span>
        )}
      </td>
      <td className="py-2 px-3 text-center">
        <span
          className={`inline-block w-2 h-2 rounded-full ${match ? "bg-success" : partial ? "bg-warning" : "bg-error"}`}
        />
      </td>
      <td className="py-2 px-3 text-sm text-white/90 max-w-[180px] truncate">
        {rightVal || (
          <span className="text-[var(--text-primary)] italic">—</span>
        )}
      </td>
      <td className="py-2 px-3 text-right text-xs font-mono">
        <span className={scoreColor(score)}>{pct(score)}</span>
      </td>
      <td className="py-2 px-3 text-right text-xs text-[var(--text-primary)] whitespace-nowrap">
        {method} · w{weight.toFixed(2)}
      </td>
    </tr>
  );
}

// ─── Pair detail panel ───────────────────────────────────────────────────────

function PairDetailPanel({
  detail,
  onDecide,
  busy,
}: {
  detail: PairDetail;
  onDecide: (
    decision: "match" | "no_match" | "escalate",
    notes?: string,
  ) => void;
  busy: boolean;
}) {
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const score = detail.composite_score;
  const allFields = Array.from(
    new Set([
      ...Object.keys(detail.left_record),
      ...Object.keys(detail.right_record),
    ]),
  );

  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto">
      {/* Score header */}
      <div className={`rounded-xl border p-4 ${scoreBg(score)}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-white/50 uppercase tracking-wider mb-1">
              Composite Score
            </p>
            <p className={`text-3xl font-bold ${scoreColor(score)}`}>
              {pct(score)}
            </p>
          </div>
          <div className="text-right text-xs text-[var(--text-primary)]">
            {detail.thresholds.auto_match_threshold != null && (
              <p>Auto-match ≥ {pct(detail.thresholds.auto_match_threshold)}</p>
            )}
            {detail.thresholds.no_match_threshold != null && (
              <p>Reject &lt; {pct(detail.thresholds.no_match_threshold)}</p>
            )}
            <p className="mt-1 font-semibold text-white/60">
              Zone: {detail.pair.zone}
            </p>
          </div>
        </div>
      </div>

      {/* Side-by-side records */}
      {allFields.length > 0 && (
        <div className="rounded-xl border border-[var(--border-muted)] overflow-hidden">
          <div className="grid grid-cols-2 divide-x divide-white/8 bg-[var(--overlay-subtle)] sticky top-0 z-10">
            <div className="px-4 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider">
              Record A
            </div>
            <div className="px-4 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider">
              Record B
            </div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-white/8 max-h-64 overflow-y-auto">
            <div className="p-3 space-y-1">
              {allFields.map((f) => (
                <div key={f} className="flex gap-2 text-sm">
                  <span className="text-[var(--text-primary)] font-mono text-xs min-w-[100px] shrink-0">
                    {f}
                  </span>
                  <span className="text-white/80 break-all">
                    {detail.left_record[f] ?? "—"}
                  </span>
                </div>
              ))}
            </div>
            <div className="p-3 space-y-1">
              {allFields.map((f) => {
                const lv = detail.left_record[f] ?? "";
                const rv = detail.right_record[f] ?? "";
                const differs = lv !== rv;
                return (
                  <div key={f} className="flex gap-2 text-sm">
                    <span className="text-[var(--text-primary)] font-mono text-xs min-w-[100px] shrink-0">
                      {f}
                    </span>
                    <span
                      className={`break-all ${differs ? "text-warning" : "text-white/80"}`}
                    >
                      {rv || "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Field breakdown table */}
      {detail.field_breakdown && detail.field_breakdown.length > 0 && (
        <div className="rounded-xl border border-[var(--border-muted)] overflow-hidden">
          <div className="px-4 py-2 bg-[var(--overlay-subtle)] text-xs font-semibold text-white/50 uppercase tracking-wider">
            Field Scores
          </div>
          <div className="overflow-auto max-h-72">
            <table className="w-full">
              <thead className="sticky top-0 bg-[var(--surface-dark)]">
                <tr className="border-b border-[var(--border-muted)] text-xs text-[var(--text-primary)]">
                  <th className="py-1.5 px-3 text-left">Field</th>
                  <th className="py-1.5 px-3 text-left">Record A</th>
                  <th className="py-1.5 px-3 text-center">≈</th>
                  <th className="py-1.5 px-3 text-left">Record B</th>
                  <th className="py-1.5 px-3 text-right">Score</th>
                  <th className="py-1.5 px-3 text-right">Method</th>
                </tr>
              </thead>
              <tbody>
                {detail.field_breakdown.map((fb) => (
                  <FieldRow
                    key={fb.field}
                    field={fb.field}
                    leftVal={fb.left_value}
                    rightVal={fb.right_value}
                    score={fb.score}
                    method={fb.method}
                    weight={fb.weight}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* LLM rationale */}
      {detail.llm_rationale && (
        <div className="rounded-xl border border-ai/20 bg-ai/8 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-ai" />
            <span className="text-xs font-semibold text-ai uppercase tracking-wider">
              AI Rationale
            </span>
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            {detail.llm_rationale}
          </p>
        </div>
      )}

      {/* RAG citations */}
      {detail.rag_citations.length > 0 && (
        <div className="rounded-xl border border-[var(--border-muted)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <Info className="w-4 h-4 text-[var(--text-primary)]" />
            <span className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider">
              KB Citations
            </span>
          </div>
          <ul className="space-y-1">
            {detail.rag_citations.map((c) => (
              <li key={c.chunk_id} className="text-xs text-white/50 italic">
                "{c.snippet}"
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Notes */}
      {showNotes && (
        <div>
          <textarea
            data-testid="mdm-pair-notes"
            className="w-full rounded-lg border border-[var(--border-muted)] bg-[var(--overlay-subtle)] p-3 text-sm text-white/80 placeholder-white/25 focus:outline-none focus:border-indigo-500/50 resize-none"
            rows={3}
            placeholder="Steward notes (required for No Match / Escalate)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      )}

      {/* Decision buttons */}
      <div className="flex gap-3 pb-4">
        <button
          disabled={busy}
          data-testid="mdm-pair-decide-match"
          onClick={() => onDecide("match", notes || undefined)}
          className="ztd-btn flex-1 gap-2 bg-success hover:opacity-90 disabled:opacity-50 py-2.5 text-sm font-semibold text-[var(--text-primary)]"
        >
          <Check className="w-4 h-4" /> Match
        </button>
        <button
          disabled={busy}
          data-testid="mdm-pair-decide-no-match"
          onClick={() => {
            if (!showNotes) {
              setShowNotes(true);
              return;
            }
            onDecide("no_match", notes || undefined);
          }}
          className="ztd-btn flex-1 gap-2 bg-error hover:opacity-90 disabled:opacity-50 py-2.5 text-sm font-semibold text-[var(--text-primary)]"
        >
          <X className="w-4 h-4" /> No Match
        </button>
        <button
          disabled={busy}
          data-testid="mdm-pair-decide-escalate"
          onClick={() => {
            if (!showNotes) {
              setShowNotes(true);
              return;
            }
            onDecide("escalate", notes || undefined);
          }}
          className="ztd-btn flex-1 gap-2 border border-warning/40 bg-warning/10 hover:bg-warning/20 disabled:opacity-50 py-2.5 text-sm font-semibold text-warning"
        >
          <AlertTriangle className="w-4 h-4" /> Escalate
        </button>
      </div>
    </div>
  );
}

// ─── Main view ───────────────────────────────────────────────────────────────

interface Props {
  runId: string;
  onBack: () => void;
}

export default function MdmPairReviewView({ runId, onBack }: Props) {
  const [queue, setQueue] = useState<PairQueueItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [queueError, setQueueError] = useState<string | null>(null);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [detail, setDetail] = useState<PairDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [decided, setDecided] = useState(0);

  // Bulk selection
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showBulkNotes, setShowBulkNotes] = useState(false);
  const [bulkNotes, setBulkNotes] = useState("");
  const allSelected = queue.length > 0 && selected.size === queue.length;
  const someSelected = selected.size > 0 && !allSelected;
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  function toggleSelect(pairId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pairId)) next.delete(pairId);
      else next.add(pairId);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected(allSelected ? new Set() : new Set(queue.map((p) => p.pair_id)));
  }

  async function handleBulkDecide(decision: "match" | "no_match" | "escalate") {
    if (!selected.size || bulkBusy) return;
    // FR-MDM-74: no_match/escalate require >=10 chars of steward reasoning,
    // enforced server-side per pair — gate on a shared notes box the same
    // way the individual pair view gates its own no_match/escalate buttons,
    // so bulk actions don't silently 422 for every pair with no way to see why.
    if (decision !== "match" && !showBulkNotes) {
      setShowBulkNotes(true);
      return;
    }
    setBulkBusy(true);
    const ids = [...selected];
    try {
      const res = await batchDecidePairs(
        ids,
        decision,
        decision === "match" ? undefined : bulkNotes,
      );
      const succeeded = res.decided.length;
      const newQueue = queue.filter(
        (p) =>
          !ids.includes(p.pair_id) ||
          res.errors.some((e) => e.pair_id === p.pair_id),
      );
      setQueue(newQueue);
      setSelected(new Set());
      setShowBulkNotes(false);
      setBulkNotes("");
      setDecided((d) => d + succeeded);
      setSelectedIndex(0);
      if (res.errors.length > 0) {
        showToast(
          `${succeeded} decided, ${res.errors.length} failed: ${res.errors[0].detail}`,
          false,
        );
      } else {
        showToast(
          `${succeeded} pairs marked as ${decision.replace("_", " ")}`,
          true,
        );
      }
      if (newQueue.length < 5 && hasMore && cursor) loadQueue(cursor);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Bulk action failed", false);
    } finally {
      setBulkBusy(false);
    }
  }

  // Load queue
  const loadQueue = useCallback(
    async (nextCursor?: string) => {
      setLoadingQueue(true);
      setQueueError(null);
      try {
        const res = await getPairQueue(runId, nextCursor);
        setQueue((prev) => (nextCursor ? [...prev, ...res.pairs] : res.pairs));
        setCursor(res.next_cursor);
        setHasMore(res.has_more);
      } catch (e) {
        setQueueError(e instanceof Error ? e.message : "Failed to load pairs");
      } finally {
        setLoadingQueue(false);
      }
    },
    [runId],
  );

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Load detail when selection changes
  useEffect(() => {
    const item = queue[selectedIndex];
    if (!item) return;
    setDetail(null);
    setLoadingDetail(true);
    getPairDetail(item.pair_id)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoadingDetail(false));
  }, [queue, selectedIndex]);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDecide = async (
    decision: "match" | "no_match" | "escalate",
    notes?: string,
  ) => {
    const item = queue[selectedIndex];
    if (!item || busy) return;
    setBusy(true);
    try {
      await submitPairDecision(item.pair_id, decision, notes);
      setDecided((d) => d + 1);
      // Remove decided pair from local queue
      const newQueue = queue.filter((_, i) => i !== selectedIndex);
      setQueue(newQueue);
      setSelectedIndex(Math.min(selectedIndex, newQueue.length - 1));
      showToast(`Recorded: ${decision.replace("_", " ")}`, true);
      // Load more if queue is getting short
      if (newQueue.length < 5 && hasMore && cursor) {
        loadQueue(cursor);
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Decision failed", false);
    } finally {
      setBusy(false);
    }
  };

  const total = queue.length + decided;

  return (
    <div className="flex flex-col" style={{ minHeight: "100%" }}>
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-[var(--border-muted)] bg-[var(--surface-dark)]/90 backdrop-blur-md">
        <div className="flex items-center gap-3 px-6 py-3.5">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white/80 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Run
          </button>
          <div className="w-px h-4 bg-[var(--overlay-strong)]" />
          <h1 className="text-sm font-semibold text-white/80">
            Steward Review Queue
          </h1>
          <div className="ml-auto flex items-center gap-3 text-xs text-[var(--text-primary)]">
            {decided > 0 && (
              <span className="font-semibold" style={{ color: "#86bc25" }}>
                {decided} decided this session
              </span>
            )}
            <span>{queue.length} remaining</span>
            <button
              onClick={() => {
                setQueue([]);
                setSelected(new Set());
                setCursor(undefined);
                setDecided(0);
                loadQueue();
              }}
              className="p-1 rounded hover:bg-[var(--overlay-medium)] transition"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Bulk action toolbar — appears when ≥1 pair is selected */}
        {selected.size > 0 && (
          <div
            className="flex items-center gap-2 px-6 py-2.5 border-t border-[var(--border-muted)]"
            style={{ background: "rgba(134,188,37,0.06)" }}
          >
            <span
              className="text-xs font-semibold"
              style={{ color: "#86bc25" }}
            >
              {selected.size} selected
            </span>
            <div className="flex-1" />
            <button
              onClick={() => handleBulkDecide("match")}
              disabled={bulkBusy}
              data-testid="mdm-pair-bulk-match"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50"
              style={{
                background: "rgba(16,185,129,0.15)",
                color: "#34d399",
                border: "1px solid rgba(16,185,129,0.3)",
              }}
            >
              <Check className="w-3.5 h-3.5" />
              Approve All ({selected.size})
            </button>
            <button
              onClick={() => handleBulkDecide("no_match")}
              disabled={bulkBusy}
              data-testid="mdm-pair-bulk-no-match"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50"
              style={{
                background: "rgba(239,68,68,0.12)",
                color: "#f87171",
                border: "1px solid rgba(239,68,68,0.25)",
              }}
            >
              <X className="w-3.5 h-3.5" />
              Reject All ({selected.size})
            </button>
            <button
              onClick={() => handleBulkDecide("escalate")}
              disabled={bulkBusy}
              data-testid="mdm-pair-bulk-escalate"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50"
              style={{
                background: "rgba(245,158,11,0.12)",
                color: "#fbbf24",
                border: "1px solid rgba(245,158,11,0.25)",
              }}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Escalate ({selected.size})
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="ml-1 p-1.5 rounded text-[var(--text-primary)] hover:text-white/60 transition"
              title="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            {bulkBusy && (
              <RefreshCw className="w-3.5 h-3.5 animate-spin text-[var(--text-primary)]" />
            )}
          </div>
        )}

        {/* Bulk steward notes — shown after a first Reject All/Escalate click;
            match ("Approve All") never requires this. */}
        {selected.size > 0 && showBulkNotes && (
          <div
            className="px-6 py-2.5 border-t border-[var(--border-muted)]"
            style={{ background: "rgba(134,188,37,0.06)" }}
          >
            <textarea
              data-testid="mdm-pair-bulk-notes"
              className="w-full rounded-lg border border-[var(--border-muted)] bg-[var(--overlay-subtle)] p-2.5 text-sm text-white/80 placeholder-white/25 focus:outline-none focus:border-indigo-500/50 resize-none"
              rows={2}
              placeholder="Steward notes (required, min 10 characters, for Reject All / Escalate)…"
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Body */}
      {loadingQueue && queue.length === 0 ? (
        <div className="py-32 flex items-center justify-center text-[var(--text-primary)] text-sm">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading pairs…
        </div>
      ) : queueError ? (
        <div className="py-32 flex flex-col items-center justify-center gap-3 text-error text-sm">
          <p>{queueError}</p>
          <button
            onClick={() => loadQueue()}
            className="text-xs text-indigo-400 hover:underline"
          >
            Retry
          </button>
        </div>
      ) : queue.length === 0 ? (
        <div
          data-testid="mdm-pair-queue-empty"
          className="py-32 flex flex-col items-center justify-center gap-3 text-[var(--text-primary)]"
        >
          <Check className="w-8 h-8 text-success" />
          <p className="text-sm font-semibold text-white/60">Queue is empty</p>
          <p className="text-xs">All pairs have been reviewed.</p>
          <button
            onClick={onBack}
            className="mt-2 text-xs text-indigo-400 hover:underline"
          >
            Return to run status
          </button>
        </div>
      ) : (
        <div className="flex" style={{ height: "calc(100vh - 180px)" }}>
          {/* Left: pair list */}
          <div className="w-72 shrink-0 border-r border-[var(--border-muted)] flex flex-col overflow-hidden">
            {/* Select-all header row */}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-[var(--border-muted)] bg-white/[0.02] shrink-0">
              <input
                ref={selectAllRef}
                type="checkbox"
                data-testid="mdm-pair-select-all"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="w-3.5 h-3.5 rounded accent-[#86BC25] cursor-pointer"
              />
              <span className="text-[11px] text-[var(--text-primary)] font-medium">
                {selected.size > 0
                  ? `${selected.size} of ${queue.length} selected`
                  : `${queue.length} pairs`}
              </span>
              {selected.size > 0 && (
                <button
                  onClick={() => setSelected(new Set())}
                  className="ml-auto text-[11px] text-[var(--text-primary)] hover:text-white/60"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {queue.map((item, i) => {
                const isChecked = selected.has(item.pair_id);
                const isActive = i === selectedIndex;
                return (
                  <div
                    key={item.pair_id}
                    className={`flex items-start gap-2 px-3 py-2.5 border-b border-[var(--border-subtle)] transition group ${
                      isActive
                        ? "border-l-2 border-l-[#86bc25]"
                        : "border-l-2 border-l-transparent"
                    } ${isChecked ? "bg-[rgba(134,188,37,0.06)]" : isActive ? "bg-white/[0.04]" : "hover:bg-white/[0.03]"}`}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      data-testid="mdm-pair-select"
                      data-pair-id={item.pair_id}
                      checked={isChecked}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelect(item.pair_id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-0.5 w-3.5 h-3.5 shrink-0 rounded accent-[#86BC25] cursor-pointer"
                    />
                    {/* Row content — click to select detail */}
                    <button
                      data-testid="mdm-pair-row"
                      data-pair-id={item.pair_id}
                      className="flex-1 text-left min-w-0"
                      onClick={() => setSelectedIndex(i)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-mono text-[var(--text-primary)]">
                          {item.pair_id.slice(0, 8)}
                        </span>
                        <span
                          className={`text-xs font-bold ${scoreColor(item.posterior_score)}`}
                        >
                          {pct(item.posterior_score)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span
                          className={`ztd-badge ${
                            item.zone === "hitl"
                              ? "ztd-badge-warning"
                              : item.zone === "auto_merge"
                                ? "ztd-badge-success"
                                : "ztd-badge-critical"
                          }`}
                        >
                          {item.zone === "hitl"
                            ? "Manual Review"
                            : item.zone === "auto_merge"
                              ? "Auto-Merge"
                              : "Reject"}
                        </span>
                        {item.llm_escalated && (
                          <span className="ztd-badge ztd-badge-ai">AI</span>
                        )}
                        {item.rereview_tag && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300 font-semibold">
                            Re-review
                          </span>
                        )}
                      </div>
                      {item.llm_rationale_preview && (
                        <p className="mt-1 text-[11px] text-[var(--text-primary)] line-clamp-2">
                          {item.llm_rationale_preview}
                        </p>
                      )}
                    </button>
                  </div>
                );
              })}
              {hasMore && (
                <button
                  onClick={() => loadQueue(cursor)}
                  disabled={loadingQueue}
                  className="w-full py-2 text-xs text-indigo-400 hover:underline disabled:opacity-50"
                >
                  {loadingQueue ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
            {/* Pagination controls */}
            <div className="border-t border-[var(--border-muted)] px-4 py-2 flex items-center justify-between shrink-0">
              <button
                disabled={selectedIndex === 0}
                onClick={() => setSelectedIndex((i) => i - 1)}
                className="p-1 rounded hover:bg-[var(--overlay-medium)] disabled:opacity-30 transition"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-[var(--text-primary)]">
                {selectedIndex + 1} / {total}
              </span>
              <button
                disabled={selectedIndex >= queue.length - 1}
                onClick={() => setSelectedIndex((i) => i + 1)}
                className="p-1 rounded hover:bg-[var(--overlay-medium)] disabled:opacity-30 transition"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Right: detail */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loadingDetail ? (
              <div className="py-20 flex items-center justify-center text-[var(--text-primary)] text-sm">
                <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading
                detail…
              </div>
            ) : detail ? (
              <PairDetailPanel
                detail={detail}
                onDecide={handleDecide}
                busy={busy}
              />
            ) : (
              <div className="py-20 flex items-center justify-center text-[var(--text-primary)] text-sm">
                Select a pair to review
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-2.5 rounded-lg text-sm font-semibold shadow-xl transition ${
            toast.ok
              ? "bg-success text-[var(--text-primary)]"
              : "bg-error text-[var(--text-primary)]"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
